import { Box, Link, List, ListItem, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { ScreenContainer, InputScreen } from 'src/components/flow';
import { ValidatedInput } from './shared/validate-input';

import type { FlowScreenProps } from './types';
import Image from 'next/image';
import { usePassword } from '../providers/password';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { getUserEmail } from 'src/redux/selectors';
import { updateUserEmail } from 'src/redux/reducers/user-data';

// see https://github.com/NerdWallet/js-auth-tools/blob/e45c007a360ae25228add4a6ef80203cac129e58/src/validation/index.js#LL11C1-L12C156
const EMAIL_FORMAT =
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const PASSWORD_VALIDATIONS = [
  {
    label: 'Be at least eight characters long',
    regex: /^.{8,}$/,
  },
  {
    label: 'Include one uppercase letter',
    regex: /[A-Z]+/,
  },
  {
    label: 'Include one number or special character',
    regex: /[\W\d]+/,
  },
];

const PasswordListItem = ({
  children,
  isValid,
}: {
  children: React.ReactNode;
  isValid: boolean;
}) => (
  <ListItem
    sx={{
      display: 'list-item',
      position: 'relative',
      ...(isValid
        ? { listStyle: 'none', color: 'green.main', marginLeft: 3 }
        : {}),
    }}
    disablePadding
  >
    {isValid && (
      <CheckIcon
        fontSize="inherit"
        sx={{
          marginRight: 1,
          position: 'absolute',
          right: '100%',
          top: '3px',
        }}
      />
    )}
    {children}
  </ListItem>
);

const PasswordRequirements = ({ password }: { password: string }) => (
  <>
    <Typography variant="bodySmall" marginBottom={0} padding={0}>
      Your password must:
    </Typography>
    <List
      sx={{
        listStyleType: 'disc',
        listStylePosition: 'inside',
      }}
    >
      {PASSWORD_VALIDATIONS.map(({ label, regex }) => (
        <PasswordListItem key={label} isValid={regex.test(password)}>
          {label}
        </PasswordListItem>
      ))}
    </List>
  </>
);
export const CtaTerms = (
  <Box
    display="flex"
    justifyContent={{ xs: 'center', md: 'flex-start' }}
    marginBottom={3}
  >
    <Typography
      variant="bodySmall"
      color="text.secondary"
      sx={(theme) => ({
        textWrap: 'nowrap',
        [theme.breakpoints.down('md')]: {
          textAlign: 'center',
          textWrap: 'wrap',
        },
      })}
    >
      By signing up, I agree to NerdWallet&apos;s{' '}
      <Link
        href="https://www.nerdwallet.com/blog/terms-of-use/"
        target="_blank"
      >
        Terms of Service
      </Link>{' '}
      and{' '}
      <Link
        href="https://www.nerdwallet.com/blog/privacy-policy/"
        target="_blank"
      >
        Privacy Policy
      </Link>
      .
    </Typography>
  </Box>
);

const HelperText = (
  <Box display={{ xs: 'none', md: 'block' }}>
    <Typography variant="textLarge">
      Stay on top of your credit cards, track your cashflow and get personalized
      recommendations on how you can save.
    </Typography>
  </Box>
);

const RegisterScreen: React.FC<FlowScreenProps> = () => {
  const dispatch = useAppDispatch();
  const email = useAppSelector(getUserEmail);
  const { password, setPassword } = usePassword();
  const isPasswordValid =
    !!password &&
    PASSWORD_VALIDATIONS.every(({ regex }) => regex.test(password));
  const isEmailValid = !!email && EMAIL_FORMAT.test(email);
  const isValid = isPasswordValid && isEmailValid;

  return (
    <ScreenContainer
      primaryCta={{
        text: 'Sign up & see my card picks',
        isDisabled: !isValid,
        terms: CtaTerms,
      }}
    >
      <InputScreen
        headline={
          <Typography
            variant="titleSmall"
            textAlign={{ xs: 'center', md: 'left' }}
          >
            See all your credit card activity in one place
          </Typography>
        }
        helperText={HelperText}
      >
        <Box
          display="flex"
          flexDirection={{ xs: 'column-reverse', md: 'row' }}
          alignItems={{ xs: 'center', md: 'flex-start' }}
          justifyContent="space-between"
          gap={{ xs: 0, md: 5 }}
        >
          <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            justifyContent="center"
            width={{ xs: '100%', md: 368 }}
          >
            <Box width="100%" marginTop={4}>
              <Typography
                component="label"
                htmlFor="email"
                display="block"
                marginBottom={1}
                variant="textSmall"
              >
                Email
              </Typography>
              <ValidatedInput
                defaultValue={email}
                autoFocus
                errorMessage="Please enter a valid email"
                id="email"
                isValid={isEmailValid}
                onChange={(value) => dispatch(updateUserEmail(value))}
              />
            </Box>
            <Box width="100%" marginTop={3}>
              <Typography
                component="label"
                htmlFor="password"
                display="block"
                marginBottom={1}
                variant="textSmall"
              >
                Password
              </Typography>
              <ValidatedInput
                defaultValue={password}
                errorMessage="Must contain 8 characters, one uppercase letter, and one number or special character"
                id="password"
                isValid={isPasswordValid}
                onChange={(value) => setPassword(value)}
                type="password"
              />
            </Box>
            <Typography component="div" variant="textSmall" marginTop={3}>
              <PasswordRequirements password={password} />
            </Typography>
          </Box>
          <Box>
            <Image
              src="https://www.nerdwallet.com/cdn/js-auth-tools/branded-elements/generic-hero-variant-full.svg"
              alt=""
              width={0}
              height={0}
              style={{ maxWidth: 585, width: '100%', height: 'auto' }}
            />
          </Box>
        </Box>
      </InputScreen>
    </ScreenContainer>
  );
};

export default RegisterScreen;
